import moment from 'moment-timezone';
import '../../node_modules/moment/dist/locale/fr.js';
moment.locale('fr');
export class DateUtil {

    public static getFrenchMonthName = (monthNumber?: string): string => {
        if(!monthNumber) {
            return '';
        }
        const date = new Date();
        date.setMonth(parseInt(monthNumber) - 1);
        const monthName = date.toLocaleString('fr-FR', { month: 'long' });

        return monthName.charAt(0).toUpperCase() + monthName.slice(1);
    };

    public static getDayNameInFrench = (dateString: string) => {

        if (!dateString) {
            return '';
        }
        const date = new Date(dateString);

        // Get the day name in French
        return date.toLocaleDateString('fr-CA', { weekday: 'long', timeZone: 'America/New_York' });
    };

    public static getDayNumber = (dateString: string) => {
        if (!dateString) {
            return '';
        }
        
        const day = new Date(dateString).toLocaleString("fr-CA", {
            timeZone: "America/New_York",
            day: "numeric"
        });
        return day;
    };

    public static getMonthNumber = (dateString: string) => {
        if (!dateString) {
            return '';
        }
        
        const month = new Date(dateString).toLocaleString("fr-CA", {
            timeZone: "America/New_York",
            month: "numeric"
        });
        return month;
    };

    public static isValidDateString = (str: string): boolean => {
        // Check format using regex
        const regex = /^\d{4}-\d{2}-\d{2}$/;
        if (!regex.test(str)) {
            return false;
        }

        // Try to create a real Date
        const date = new Date(str);
        if (Number.isNaN(date.getTime())) {
            return false;
        }

        // Extra: check if the parts match (avoid JS weird autocorrections)
        const [year, month, day] = str.split('-').map(Number);
        return (
            date.getUTCFullYear() === year &&
            date.getUTCMonth() + 1 === month &&
            date.getUTCDate() === day
        );
    };

    public static createLocalDateFromYMD = (ymdString: string): Date => {
        const [year, month, day] = ymdString.split('-').map(Number);
        return new Date(year, month - 1, day); // Month is 0-indexed!
    };


    public static formatDateToLocalYMD = (date: Date | null): string => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // JS months 0-11
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    public static convertToESTLong = (dateString: string, timeUnkown: boolean = false) => {
        if (!dateString) {
            return '';
        }

        // Create a Date object from the input string
        const date = new Date(dateString);

        const options = {
            timeZone: 'America/New_York',
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: timeUnkown ? undefined : 'numeric',
            minute: timeUnkown ? undefined : '2-digit'
        };
        return date.toLocaleDateString('fr-CA', options as any);
    }

    public static convertToEST = (dateString: string, timeUnkown: boolean = false) => {

        if (!dateString) {
            return '';
        }

        // Create a Date object from the input string
        const date = new Date(dateString);

        // Convert to EST timezone
        const options = {
            timeZone: 'America/New_York',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        };

        // Format as YYYY-MM-DD HH:MM:SS in EST
        const estDate = date.toLocaleString('fr-CA', options as any);

        // Reformat the date
        const [year, month, day, hour, minute] = estDate.match(/\d+/g) || [];
        let time = timeUnkown ? '' : `${hour}h${parseInt(minute) > 0 ? minute : ''}`;
        return `${day}/${month}/${year} ${time}`;

    }

    public static formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0'); // months are 0-based
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    public static toDatetimeLocal = (date: Date) => {
        const pad = (n: number) => String(n).padStart(2, "0");
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }

    public static getWeekDays = (nextWeek?: boolean) => {
        const today = new Date();
        if (nextWeek) {
            today.setDate(today.getDate() + 7);
        }
        const dayOfWeek = today.getDay(); // 0 (Sun) - 6 (Sat)
        const distanceToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Sunday
        const monday = new Date(today);
        monday.setDate(today.getDate() - distanceToMonday);

        // Calculate Sunday (7 days after Monday)
        const sunday = new Date(monday);
        sunday.setDate(monday.getDate() + 6);

        // Format as YYYY-MM-DD
        const formatDate = (date: Date) => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };

        return {
            monday: formatDate(monday),
            sunday: formatDate(sunday)
        };
    }

     public static getPeriodLabel = (
        start: string,
        end: string,
        tz = "America/Toronto",
        locale = "fr"
    ) => {
        let s = moment.tz(start, tz);
        let e = moment.tz(end, tz);

        // Safety: if end < start, swap
        if (e.isBefore(s)) [s, e] = [e, s];

        s = s.locale(locale);
        e = e.locale(locale);

        const sameYear = s.year() === e.year();
        const sameMonth = sameYear && s.month() === e.month();

        if (sameYear) {
            // e.g., "25–31 août 2025" or "28 août – 3 septembre 2025"
            return sameMonth
                ? `${s.format("D")}–${e.format("D MMMM YYYY")}`
                : `${s.format("D MMMM")} – ${e.format("D MMMM YYYY")}`;
        }

        // Cross-year: show the year on BOTH sides
        // e.g., "25 décembre 2025 – 5 janvier 2026"
        return `${s.format("D MMMM YYYY")} – ${e.format("D MMMM YYYY")}`;
    };

}